import { useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { apiGet, getAccessToken } from "@/api/client";
import { rolesFromAccessToken } from "@/lib/prp-home";
import { PRP_ENABLED } from "@/lib/hidden-features";

interface EffectivePermissionsResponse {
  permissions: string[];
}

// Errors are not swallowed: caching an empty list after a failed call (e.g.
// mid token refresh) would hide screens and redirect away for 5 minutes.
async function getEffectivePermissions(): Promise<string[]> {
  const data = await apiGet<EffectivePermissionsResponse>("/admin/me/permissions");
  return data.permissions ?? [];
}

function tokenImpliesPrp(resource: string): boolean {
  if (resource !== "prp") return false;
  return rolesFromAccessToken(getAccessToken()).some(
    (role) => role === "prp_rc" || role === "prp_director",
  );
}

/** UI capability helper. Server-side RBAC remains authoritative. */
export function usePermissions() {
  const query = useQuery({
    queryKey: ["auth", "effective-permissions"],
    queryFn: getEffectivePermissions,
    staleTime: 5 * 60 * 1000,
  });

  const can = useCallback(
    (resource: string, action: string): boolean => {
      if (!PRP_ENABLED && (resource === "prp" || resource.startsWith("prp_"))) return false;
      const permissions = query.data ?? [];
      // PRP roles get PRP screens before /admin/me/permissions answers; once it
      // does, its list decides, so counselors don't see Director-only actions.
      if (!permissions.length && tokenImpliesPrp(resource)) return true;
      return (
        permissions.includes("*") ||
        permissions.includes(`${resource}:*`) ||
        permissions.includes(`${resource}:${action}`)
      );
    },
    [query.data],
  );

  return { ...query, can };
}

